import React, { useContext } from 'react';
import { shape, string, arrayOf } from 'prop-types';
import classnames from 'classnames';
import { Pill } from '@andes/badge';
import StaticPropsContext from '../context/static-props';
import StyledLabel from '../styled-label';
import Action from '../buybox/actions/action';
import { validateSnackbarColor } from '../../utils/validators';
import IconFactory from '../icons/factory-builder';
import colornames from '../../lib/colornames';
import VehicleInspectionAttribute from '../vehicle-inspection-attribute';

const namespace = 'ui-pdp-vehicle-condition';

const renderBadge = ({ text, type, font_size: fontSize, hierarchy }) => (
  <Pill
    className={`${namespace}__pill`}
    color={validateSnackbarColor(type.toLowerCase())}
    size={fontSize.toLowerCase()}
    hierarchy={hierarchy.toLowerCase()}
    roundedCorners={['top-right', 'top-left', 'bottom-right', 'bottom-left']}
  >
    {text}
  </Pill>
);

const renderCard = ({ title, description, icon }) => (
  <div className={`${namespace}__card`}>
    <div className={`${namespace}__card-text`}>
      <StyledLabel className={`${namespace}__card-title`} {...title} />
      <StyledLabel className={`${namespace}__card-description`} {...description} />
    </div>
    <div aria-hidden="true" className={`${namespace}__card-icon`}>
      {IconFactory(icon.id, classnames(colornames(icon.color)))}
    </div>
  </div>
);

const VehicleCondition = ({
  header,
  badge,
  description,
  card,
  sections,
  summary_label: summaryLabel,
  show_status_action: showStatusAction,
}) => {
  const { deviceType } = useContext(StaticPropsContext);

  return (
    <div className={namespace}>
      <StyledLabel as="h2" className={`${namespace}__header`} {...header}>
        {badge && renderBadge(badge)}
      </StyledLabel>
      {description && <StyledLabel as="p" className={`${namespace}__description`} {...description} />}
      {card && renderCard(card)}
      {sections.map(({ id: sectionId, attributes }) => (
        <div key={sectionId} className={`${namespace}__section`}>
          <div className={`${namespace}__section-attributes`}>
            {attributes.map(attr => (
              <VehicleInspectionAttribute key={attr.label.text} {...attr} />
            ))}
          </div>
        </div>
      ))}
      {summaryLabel && <StyledLabel className={`${namespace}__summary-label`} {...summaryLabel} />}
      {showStatusAction && (
        <Action
          {...showStatusAction}
          actionType={showStatusAction.action_type}
          hierarchy={deviceType === 'mobile' && 'actionRow'}
          className={`${namespace}__action`}
        />
      )}
    </div>
  );
};

VehicleCondition.propTypes = {
  header: shape({
    text: string.isRequired,
    color: string,
    font_size: string,
    font_family: string,
  }).isRequired,
  badge: shape({
    text: string,
    type: string,
    font_size: string,
    hierarchy: string,
    border: string,
  }),
  description: shape({
    text: string,
    color: string,
    font_size: string,
    font_family: string,
  }),
  card: shape({
    title: shape({
      text: string.isRequired,
      color: string,
      font_size: string,
      font_family: string,
    }).isRequired,
    description: shape({
      text: string.isRequired,
      color: string,
      font_size: string,
      font_family: string,
    }).isRequired,
    icon: shape({
      id: string.isRequired,
      color: string,
      size: string,
    }).isRequired,
  }),
  sections: arrayOf(
    shape({
      id: string.isRequired,
      attributes: arrayOf(
        shape({
          icon: shape({
            id: string.isRequired,
            color: string,
            size: string,
          }).isRequired,
          label: shape({
            text: string.isRequired,
            color: string,
            font_size: string,
            font_family: string,
          }).isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  summary_label: shape({
    text: string,
    color: string,
    font_size: string,
    font_family: string,
  }),
  show_status_action: shape({
    label: shape({
      text: string,
    }),
    action_type: string,
  }),
};

VehicleCondition.displayName = 'VehicleCondition';

export default React.memo(VehicleCondition);
